import { useEffect, useState } from 'react';

/**
 * London, UK — the map center used until (and unless) a better one is known.
 * Shared by the fleet map and the mission planner so both open the same way.
 */
export const DEFAULT_MAP_CENTER: [number, number] = [51.5074, -0.1278];

/** Tuned for a one-shot "roughly where am I" fix, not for navigation. */
const GEOLOCATION_OPTIONS: PositionOptions = {
  enableHighAccuracy: false,
  timeout: 10_000,
  maximumAge: 5 * 60_000,
};

/**
 * One-shot device location for the initial map center (issue #401).
 *
 * Returns null until the browser reports a position, and keeps returning null
 * when geolocation is unsupported, denied, or times out — callers stay on
 * `DEFAULT_MAP_CENTER`. Silent by design: this is a convenience, and a
 * permission the operator declined is not a failure worth surfacing.
 *
 * @param enabled pass false to skip the permission prompt entirely — e.g. the
 *   map already has real position context of its own to center on.
 */
export const useDeviceLocation = (enabled = true): [number, number] | null => {
  const [position, setPosition] = useState<[number, number] | null>(null);

  useEffect(() => {
    if (!enabled || !navigator.geolocation) {
      return;
    }

    let active = true;
    navigator.geolocation.getCurrentPosition(
      (fix) => {
        if (active) {
          setPosition([fix.coords.latitude, fix.coords.longitude]);
        }
      },
      // Denied, unavailable, or timed out — the default center still works.
      () => undefined,
      GEOLOCATION_OPTIONS
    );

    return () => {
      active = false;
    };
  }, [enabled]);

  return position;
};
